import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, Battery, RefreshCcw, ShieldCheck, X } from "lucide-react";
import l3 from "../assets/drone-repair.jpeg";
import l2 from "../assets/Services_Drone-Health-Check.webp";
import l4 from "../assets/drone-repair2.png";
import GridComp from "../Components/GridComp";
import ReusableBanner from "../Components/Banner";
import HeadingBlock from "../Components/HeadingBlock";
import { Quote } from "lucide-react";
import FeaturesGrid from "../Components/FeaturesGrid";
import { Helmet } from "react-helmet";


const features = [
  {
    icon: <Wrench className="w-10 h-10 text-green-600" />,
    title: "Complete Inspection",
    desc: "Every motor, blade, wire and sensor are checked by experts.",
  },
  {
    icon: <Battery className="w-10 h-10 text-green-600" />,
    title: "Battery & Power Testing",
    desc: "We ensure your drone never loses energy in the air.",
  },
  {
    icon: <RefreshCcw className="w-10 h-10 text-green-600" />,
    title: "Software Updates",
    desc: "Stay updated with latest features & smooth performance.",
  },
  {
    icon: <ShieldCheck className="w-10 h-10 text-green-600" />,
    title: "Preventive Maintenance",
    desc: "Identify problems before they become costly repairs.",
  },
];

const DroneHealthCheck = () => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 10; // tolerance for smoothness

      if (scrolledToBottom) {
        setShowModal(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);


  return (
    <>
      <Helmet>
        <title>Drone Health Check: Keep Your Drone Healthy</title>

        <meta
          name="description"
          content="At Drone Didi, we provide complete inspection of your drone, battery & power testing, drone’s software updates and preventive maintenance."
        />

        <link
          rel="canonical"
          href="https://dronedidi.co.in/drone_health_check"
        />
      </Helmet>
      <ReusableBanner
        bgImage={l2}
        title={
          <span>
            Drone Health Check - Keep Your{" "}
            <span className="text-orange-400">Wings</span> Strong
          </span>
        }
        subtitle="A healthy drone, a happy Didi."
        height="h-[60vh] md:h-[70vh]"
        fixedAttachment={true}
        align="right"
      />

      <div className="max-w-7xl mx-auto p-6">
        <div className="py-10">
          <GridComp
            title="Why a Health Check"
            highlight="Matters?"
            paragraphs={[
              "Just like your health is important, so is the health of your drone. Small problems, if ignored, can stop you mid-flight. Our drone health check ensures your machine is always ready to work when you need it most.",
            ]}
            image={l3}
            align="left"
            reverse={true}
            titleColor="text-green-950"
            highlightColor="text-orange-400"
          />
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-16 px-6 lg:px-16">
   
  <HeadingBlock
        subtitle="Our Support"
        title="Here’s How We Support You"
          align="center"

        // highlight="Agency"
      />

        <FeaturesGrid
        features={features}
        cols={4} // ✅ choose 2 / 3 / 4 / 5 dynamically
        textColor="text-green-900"
        descColor="text-gray-700"
        iconColor="text-orange-500"
        dividerFrom="after:from-green-200"
        dividerTo="after:to-green-900"
      />
      </div>
  <section className="relative bg-gradient-to-t from-white border-t-8 border-dashed border-green-950 via-green-100 to-green-50 py-16 px-6 md:px-20">
    
  <HeadingBlock
        subtitle="Why It Matters"
        title=" Think of your drone as more than just a machine; it’s your trusted co-pilot in the field. 
        With a regular health check, you’re not only protecting your investment but also ensuring 
        that "
          align="center"

        highlight="your farm operations never face sudden interruptions."
      />
      {/* Highlighted Quote Box */}
      <div className="relative max-w-4xl mx-auto mt-10">
        <Quote className="absolute -top-6 -left-6 text-orange-300 opacity-30 w-20 h-20 z-30 rotate-180" />
        <div className=" rounded-2xl p-8 md:p-12 relative z-10">
          <p className="text-xl md:text-2xl font-medium text-gray-800 italic leading-relaxed text-center">
            "Your drone is your partner in the field, not just a tool. 
            We ensure that it remains robust, dependable, and prepared 
            for any mission with our health check.”
          </p>
        </div>
        <Quote className="absolute -bottom-6 -right-6 text-orange-300 opacity-30 w-20 h-20  z-30" />
      </div>
    </section>
      {/* Popup Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Close button outside modal */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg z-50"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>

            <motion.div
              className="bg-gradient-to-r from-green-300 to-green-50 rounded-2xl shadow-2xl max-w-3xl w-[90%] p-8 relative grid md:grid-cols-2 gap-6 items-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Left: Image */}
              <div className="flex justify-center">
                <img
                  src={l4}
                  alt="Drone Health Check"
                  className="rounded-xl object-cover h-64 md:h-80 w-full"
                />
              </div>

              {/* Right: Text */}
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-green-700 mb-4">
                  Your Partner in the Field
                </h3>
                <p className="text-gray-600 mb-6">
                  “Your drone is your partner in the field, not just a tool. We
                  ensure that it remains robust, dependable, and prepared for
                  any mission with our health check.”
                </p>

                <button className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold rounded-xl hover:shadow-lg transition">
                  Book Now
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DroneHealthCheck;
